import React, { memo, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { useDrop } from 'react-dnd';

import { useSelectList } from 'components/context/SelectListContext';

import { DragProps, DropProps } from './interfaces';

const Drop = <T extends DragProps<{}>>({
  accept,
  className,
  children,
  renderIdx,
  renderPreview,
  onDrop,
  droppable = () => true,
}: PropsWithChildren<DropProps<T>>) => {
  const [{ isOver, canDrop, item }, drop] = useDrop(
    {
      accept,
      drop: onDrop,
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: droppable(monitor.getItem()) && monitor.canDrop(),
        item: monitor.getItem(),
      }),
      canDrop: item => droppable(item),
    },
    [droppable]
  );

  const selectedList = useSelectList();

  const isDndTypeMatch = accept.includes(item?.type);
  const isDroppable = isOver && canDrop;
  const isActive =
    isDroppable &&
    isDndTypeMatch &&
    (selectedList?.selectedIds.length > 1
      ? true
      : renderIdx !== item.index && renderIdx !== item.index + 1);

  return (
    <div className={classNames('drop', className)} ref={drop} data-testid="drop">
      {children}
      {renderPreview?.({ canDrop, isActive, isOver: isDroppable, item })}
    </div>
  );
};

export default memo(Drop);
